import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';

import getLogoWrapperCss, { classes } from './getLogoWrapperCss';

export type Props = {
  /**
   * The logo content to wrap. This can be an image, SVG, or any other React element.
   */
  children: React.ReactNode;

  /**
   * The size of the logo wrapper.
   * @default medium
   * @deprecated use `paddingVariant` instead
   */
  size?: 'small' | 'medium';
  /**
   * Controls the padding and negative margin.
   * By default, we assume the logo has no padding in the image
   * and we add some padding in dark mode in addition to white background.
   * But some logos have padding in the image,
   * in that case you can use `noPadding` to remove the extra padding.
   * @default start
   * @deprecated use `paddingVariant` instead
   */
  edgeAlign?: 'start' | 'noPadding';
  /**
   * Controls the padding and negative margin.
   * @default none
   */
  paddingVariant?:
    | 'none'
    | 'small'
    | 'medium'
    | 'smallOutside'
    | 'mediumOutside';
} & BaseComponentProps<'div'>;

/**
 * A wrapper component that adds a white background and padding to logos in dark mode.
 * This is useful for logos that don't have proper contrast on dark backgrounds.
 *
 * See [Props](__storybookUrl__/components-data-display-logowrapper--default#props)
 */
const LogoWrapper = (props: Props) => {
  const {
    size = 'medium',
    children,
    className,
    edgeAlign = 'start',
    paddingVariant,
    ...rest
  } = props;

  return (
    <div
      className={clsx(className, classes.base, {
        [classes.noPadding]:
          paddingVariant === 'none' ||
          (!paddingVariant && edgeAlign === 'noPadding'),
        [classes.smallPaddingInside]: paddingVariant === 'small',
        [classes.smallPaddingOutside]:
          paddingVariant === 'smallOutside' ||
          (!paddingVariant && size === 'small' && edgeAlign === 'start'),
        [classes.mediumPaddingInside]: paddingVariant === 'medium',
        [classes.mediumPaddingOutside]:
          paddingVariant === 'mediumOutside' ||
          (!paddingVariant && size === 'medium' && edgeAlign === 'start'), // this is the default for backward compatibility
      })}
      css={getLogoWrapperCss()}
      {...rest}
    >
      {children}
    </div>
  );
};

export default LogoWrapper;
